import { useEffect, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import { useToggle } from '../../hooks/useToggle';
import PageMeta from '../../components/common/PageMeta';
import { toast } from 'react-toastify';
import CommonTable, {
  ColumnConfig,
  ActionConfig,
} from '../../components/common/Table';
import { TCommunityForum } from '../../types/community-forum';
import {
  getCommunityForumList,
  updateForumStatus,
} from '../../services/community-forum';
import { Modal } from '../../components/ui/modal';

const columns: ColumnConfig<TCommunityForum>[] = [
  {
    key: 'id',
    header: 'ID',
    render: (item: TCommunityForum) => item.id,
  },
  {
    key: 'title',
    header: 'Title',
  },
  {
    key: 'content',
    header: 'Content',
    className: 'max-w-64 truncate',
    render: (item: TCommunityForum) => (
      <span title={item.content}>{item.content}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (item: TCommunityForum) => (
      <span
        className={`px-2 py-1 rounded-full text-xs ${
          item.status === 'approved'
            ? 'bg-green-100 text-green-800'
            : item.status === 'rejected'
            ? 'bg-red-100 text-red-800'
            : 'bg-yellow-100 text-yellow-800'
        }`}
      >
        {item.status}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Created At',
    render: (item: TCommunityForum) =>
      new Date(item.createdAt).toLocaleString(),
  },
  {
    key: 'approvedAt',
    header: 'Approved At',
    render: (item: TCommunityForum) =>
      item.approvedAt ? new Date(item.approvedAt).toLocaleString() : 'N/A',
  },
];

const CommunityForum = () => {
  const [forums, setForums] = useState<TCommunityForum[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedForum, setSelectedForum] = useState<TCommunityForum | null>(
    null
  );
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const { state: isLoading, on: showLoader, off: hideLoader } = useToggle();

  const fetchForums = async (page: number) => {
    if (isLoading) return;
    showLoader();
    try {
      const res = await getCommunityForumList(page, 10);
      setForums(res?.data?.rows ?? []);
      setTotalPages(Math.ceil((res?.data?.count ?? 0) / 10));
    } catch (error) {
      toast.error('Failed to fetch forums');
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchForums(newPage);
  };

  const handleStatusChange = async (
    forum: TCommunityForum,
    newStatus: 'approved' | 'rejected'
  ) => {
    try {
      showLoader();
      await updateForumStatus(forum.id, newStatus);
      toast.success(`Forum ${newStatus} successfully`);
      fetchForums(currentPage);
    } catch (error) {
      toast.error('Failed to update forum status');
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  const handleViewForum = (forum: TCommunityForum) => {
    setSelectedForum(forum);
    setIsViewModalOpen(true);
  };

  const actions: ActionConfig<TCommunityForum>[] = [
    {
      label: 'Approve',
      onClick: (forum) => handleStatusChange(forum, 'approved'),
      variant: 'primary',
      size: 'sm',
      className: (forum: TCommunityForum) =>
        forum.status === 'approved' ? 'opacity-50 cursor-not-allowed' : '',
    },
    {
      label: 'Reject',
      onClick: (forum) => handleStatusChange(forum, 'rejected'),
      variant: 'outline',
      size: 'sm',
      className: (forum: TCommunityForum) =>
        forum.status === 'rejected' ? 'opacity-50 cursor-not-allowed' : '',
    },
    {
      label: 'View',
      onClick: handleViewForum,
      variant: 'outline',
      size: 'sm',
    },
  ];

  useEffect(() => {
    fetchForums(1);
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <PageMeta title="Community Forum" description="Community forum posts" />
      <PageBreadcrumb pageTitle="Community Forum" />
      <CommonTable
        data={forums}
        columns={columns}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        showPagination={true}
        actions={actions}
      />

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        className="max-w-[700px]"
      >
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Forum Details</h2>
          {selectedForum && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-700">Title</h3>
                <p className="text-gray-900 border-2 border-gray-200 rounded-md p-2">
                  {selectedForum.title}
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-700">
                  Course Name
                </h3>
                <p className="text-gray-900 border-2 border-gray-200 rounded-md p-2">
                  {selectedForum?.course.name}
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-700">Content</h3>
                <p className="text-gray-900 whitespace-pre-wrap border-2 border-gray-200 rounded-md p-2">
                  {selectedForum.content}
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-700">
                  Post Created By
                </h3>
                <p className="text-gray-900 whitespace-pre-wrap border-2 border-gray-200 rounded-md p-2">{`${selectedForum.user.name} (${selectedForum.user.email})`}</p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-700">Status</h3>
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    selectedForum.status === 'approved'
                      ? 'bg-green-100 text-green-800'
                      : selectedForum.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {selectedForum.status}
                </span>
              </div>
              {selectedForum.rejectionReason && (
                <div>
                  <h3 className="text-lg font-medium text-gray-700">
                    Rejection Reason
                  </h3>
                  <p className="text-gray-900 border-2 border-gray-200 rounded-md p-2">
                    {selectedForum.rejectionReason}
                  </p>
                </div>
              )}
              <div>
                <h3 className="text-lg font-medium text-gray-700">
                  Created At
                </h3>
                <p className="text-gray-900 border-2 border-gray-200 rounded-md p-2">
                  {new Date(selectedForum.createdAt).toLocaleString()}
                </p>
              </div>
              {selectedForum.approvedAt && (
                <div>
                  <h3 className="text-lg font-medium text-gray-700">
                    Approved At
                  </h3>
                  <p className="text-gray-900 border-2 border-gray-200 rounded-md p-2">
                    {new Date(selectedForum.approvedAt).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CommunityForum;
